import { useQuery } from '@tanstack/react-query'
import { CreditCard, User, Loader2 } from 'lucide-react'
import { AppShell } from '@/components/shell'
import { useAuth } from '@/context/AuthContext'
import { supabase } from '@/integrations/supabase/client'

export default function Billing() {
  const { user, currentMembership } = useAuth()
  const leagueId = currentMembership?.league_id
  const isOwner = currentMembership?.role === 'owner'

  const { data: league, isLoading: isLoadingLeague } = useQuery({
    queryKey: ['billing-league', leagueId],
    queryFn: async () => {
      const { data } = await supabase
        .from('leagues')
        .select('id, name, created_at, settings')
        .eq('id', leagueId!)
        .single()
      return data
    },
    enabled: !!leagueId,
  })

  const { data: myPayment } = useQuery({
    queryKey: ['billing-my-payment', leagueId, user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('league_memberships')
        .select('payment_status')
        .eq('league_id', leagueId!)
        .eq('user_id', user!.id)
        .single()
      return data
    },
    enabled: !!leagueId && !!user?.id && !isOwner,
  })

  const { data: owner } = useQuery({
    queryKey: ['billing-owner', leagueId],
    queryFn: async () => {
      const { data } = await supabase
        .from('league_memberships')
        .select('display_name')
        .eq('league_id', leagueId!)
        .eq('role', 'owner')
        .single()
      return data
    },
    enabled: !!leagueId && !isOwner,
  })


  if (!currentMembership || !leagueId) {
    return (
      <AppShell>
        <div className="max-w-lg mx-auto px-4 py-12 text-center">
          <p className="text-zinc-500 dark:text-zinc-400">No league selected</p>
        </div>
      </AppShell>
    )
  }

  if (isLoadingLeague) {
    return (
      <AppShell>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
        </div>
      </AppShell>
    )
  }

  const settings = league?.settings as Record<string, unknown> | null
  const leagueCost = (settings?.league_cost as number) ?? 20
  const paymentStatus = (settings?.payment_status as string) ?? 'waived'

  return (
    <AppShell>
      <div className="max-w-lg mx-auto px-4 py-8 space-y-6">
        <div>
          <h1 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">Billing</h1>
          <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">{league?.name ?? 'League'}</p>
        </div>

        {isOwner ? (
          <OwnerBillingView
            leagueName={league?.name ?? ''}
            createdAt={league?.created_at ?? ''}
            leagueCost={leagueCost}
            paymentStatus={paymentStatus}
          />
        ) : (
          <MemberBillingView
            ownerName={owner?.display_name ?? 'the league owner'}
            paymentStatus={myPayment?.payment_status ?? currentMembership?.payment_status ?? 'pending'}
          />
        )}
      </div>
    </AppShell>
  )
}

function OwnerBillingView({
  leagueName,
  createdAt,
  leagueCost,
  paymentStatus,
}: {
  leagueName: string
  createdAt: string
  leagueCost: number
  paymentStatus: string
}) {
  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 p-5 space-y-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center h-10 w-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30">
            <CreditCard className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div>
            <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">League Creation Fee</p>
            <p className="text-xs text-zinc-500">{leagueName}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-xs text-zinc-500">Amount</p>
            <p className="font-medium text-zinc-900 dark:text-zinc-100">${leagueCost.toFixed(2)}</p>
          </div>
          <div>
            <p className="text-xs text-zinc-500">Status</p>
            <span className={`inline-flex items-center px-2 py-0.5 text-xs rounded-full font-medium ${
              paymentStatus === 'paid'
                ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'
                : paymentStatus === 'waived'
                ? 'bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400'
                : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
            }`}>
              {paymentStatus}
            </span>
          </div>
          <div>
            <p className="text-xs text-zinc-500">Created</p>
            <p className="font-medium text-zinc-900 dark:text-zinc-100">
              {new Date(createdAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
          </div>
        </div>
      </div>

      {paymentStatus === 'waived' && (
        <div className="rounded-lg bg-zinc-50 dark:bg-zinc-800/50 px-4 py-3 text-xs text-zinc-500 dark:text-zinc-400">
          Payment is currently waived for all leagues. Stripe checkout will be enabled in a future update.
        </div>
      )}
    </div>
  )
}

function MemberBillingView({
  ownerName,
  paymentStatus,
}: {
  ownerName: string
  paymentStatus: string
}) {
  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 p-5 space-y-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center h-10 w-10 rounded-full bg-zinc-100 dark:bg-zinc-800">
            <User className="h-5 w-5 text-zinc-500" />
          </div>
          <div>
            <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">League managed by {ownerName}</p>
            <p className="text-xs text-zinc-500">Contact them for payment questions</p>
          </div>
        </div>

        {paymentStatus && paymentStatus !== 'pending' && (
          <div className="text-sm">
            <p className="text-xs text-zinc-500">Your payment status</p>
            <span className={`inline-flex items-center px-2 py-0.5 text-xs rounded-full font-medium mt-1 ${
              paymentStatus === 'paid'
                ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'
                : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400'
            }`}>
              {paymentStatus}
            </span>
          </div>
        )}
      </div>
    </div>
  )
}
